
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { format } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { CalendarIcon } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { BusinessFormData } from './types';

interface GeneralInfoTabProps {
  formData: BusinessFormData;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectChange: (value: string, field: string) => void;
  date?: Date;
  setDate: (date: Date | undefined) => void;
  calendarOpen: boolean;
  setCalendarOpen: (open: boolean) => void;
  handleDateSelect: (date: Date | undefined) => void;
  displayNameManuallyEdited: boolean;
}

export const GeneralInfoTab: React.FC<GeneralInfoTabProps> = ({
  formData,
  handleInputChange,
  handleSelectChange,
  date,
  setDate,
  calendarOpen,
  setCalendarOpen,
  handleDateSelect,
  displayNameManuallyEdited
}) => {
  return (
    <div className="space-y-6">
      {/* Basic Info Container */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Row 1: Business Name | Display Name | Website */}
          <div className="space-y-2">
            <Label htmlFor="name">Business Name</Label>
            <Input 
              id="name" 
              name="name" 
              value={formData.name} 
              onChange={handleInputChange} 
              placeholder="Business name" 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="displayName">Display Name</Label>
            <Input 
              id="displayName" 
              name="displayName" 
              value={formData.displayName} 
              onChange={handleInputChange} 
              placeholder="Display name" 
              maxLength={30}
            />
            <div className="flex justify-between">
              <p className="text-xs text-gray-500">
                {formData.displayName.length}/30 characters
              </p>
              {displayNameManuallyEdited && (
                <p className="text-xs text-amber-600">
                  Manually edited
                </p>
              )}
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="websiteUrl">Website</Label>
            <Input 
              id="websiteUrl" 
              name="websiteUrl" 
              value={formData.websiteUrl} 
              onChange={handleInputChange} 
              placeholder="https://website.com" 
            />
          </div>

          {/* Row 2: Business Phone | Business Email | Date Founded */}
          <div className="space-y-2">
            <Label htmlFor="primaryPhone">Business Phone</Label>
            <Input 
              id="primaryPhone" 
              name="primaryPhone" 
              value={formData.primaryPhone} 
              onChange={handleInputChange} 
              placeholder="(555) 555-5555" 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="primaryEmail">Business Email</Label>
            <Input 
              id="primaryEmail" 
              name="primaryEmail" 
              value={formData.primaryEmail} 
              onChange={handleInputChange} 
              placeholder="business@example.com" 
              type="email"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="openingDate">Opening Date</Label>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !date && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? format(date, "PPP") : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={handleDateSelect}
                  initialFocus
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>
      
      {/* Separator between sections */}
      <Separator className="my-6" />
      
      {/* Industry Container */}
      <div className="space-y-4 bg-gray-50 p-4 rounded-md">
        <h3 className="text-lg font-medium">Industry Information</h3>
        <div className="grid grid-cols-1 gap-4">
          <div className="space-y-2">
            <Label htmlFor="industry">Industry</Label>
            <Input 
              id="industry" 
              value={formData.industry} 
              readOnly 
              disabled 
              className="bg-gray-100" 
            />
          </div>
        </div>
      </div>
    </div>
  );
};
